import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Close,
  Book,
  Email,
  Phone,
  Chat,
  QuestionAnswer,
  Support,
} from "@mui/icons-material";
import { trackEvent } from "../utils/analytics";

interface SupportTicket {
  name: string;
  email: string;
  subject: string;
  message: string;
  priority: "low" | "medium" | "high";
  category: string;
}

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQ[] = [
  {
    question: "How do I get started with Keep Kid Busy?",
    answer:
      "Simply sign up with your email, complete the family profile questionnaire, and you'll receive personalized activity recommendations based on your kids' ages and interests.",
    category: "Getting Started",
  },
  {
    question: "Are the activities suitable for all ages?",
    answer:
      "Yes! We have activities for kids from 2-18 years old. Each activity is tagged with age-appropriate ranges and difficulty levels.",
    category: "Activities",
  },
  {
    question: "How often do you add new activities?",
    answer:
      "We add 10-15 new activities every week, plus seasonal and holiday-themed content throughout the year.",
    category: "Content",
  },
  {
    question: "Can I share activities with other dads?",
    answer:
      "Absolutely! Use our social sharing features to share your favorite activities with friends and family.",
    category: "Sharing",
  },
  {
    question: "Is Keep Kid Busy completely free?",
    answer:
      "Yes, our core platform is 100% free. We offer premium features for advanced personalization and exclusive content.",
    category: "Pricing",
  },
  {
    question: "How do I contact customer support?",
    answer:
      "You can reach us through this support widget, email us at support@keepkidbusy.com, or call us at 1-800-KEEP-KID.",
    category: "Support",
  },
];

const supportCategories = [
  "Getting Started",
  "Activities",
  "Account Issues",
  "Technical Problems",
  "Billing",
  "Feature Requests",
  "Other",
];

const CustomerSupport: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"faq" | "contact" | "resources">(
    "faq"
  );
  const [ticket, setTicket] = useState<SupportTicket>({
    name: "",
    email: "",
    subject: "",
    message: "",
    priority: "medium",
    category: "Getting Started",
  });
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    trackEvent("support_widget_opened");
  };

  const handleClose = () => {
    setOpen(false);
    setActiveTab("faq");
    setSubmitSuccess(false);
    setTicket({
      name: "",
      email: "",
      subject: "",
      message: "",
      priority: "medium",
      category: "Getting Started",
    });
  };

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // trackFormSubmission("support_ticket", {
      //   category: ticket.category,
      //   priority: ticket.priority,
      //   subject: ticket.subject,
      // });

      setSubmitSuccess(true);
      setSubmitting(false);

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setActiveTab("faq");
      }, 3000);
    } catch (error) {
      // Handle error
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof SupportTicket, value: string) => {
    setTicket((prev) => ({ ...prev, [field]: value }));
  };

  const renderFAQ = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Frequently Asked Questions
      </Typography>
      {/* <Stack spacing={2}> */}
      {faqs.map((faq, index) => (
        // <Accordion
        //   key={index}
        //   sx={{
        //     boxShadow: "none",
        //     border: `1px solid ${theme.palette.divider}`,
        //   }}
        // >
        //   <AccordionSummary expandIcon={<ExpandMore />}>
        //     <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        //       {faq.question}
        //     </Typography>
        //   </AccordionSummary>
        //   <AccordionDetails>
        //     <Typography variant="body2" color="text.secondary">
        //       {faq.answer}
        //     </Typography>
        //     <Chip
        //       label={faq.category}
        //       size="small"
        //       sx={{ mt: 1 }}
        //       color="primary"
        //       variant="outlined"
        //     />
        //   </AccordionDetails>
        // </Accordion>
        <Box key={index} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {faq.question}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {faq.answer}
          </Typography>
          {/* <Chip
            label={faq.category}
            size="small"
            sx={{ mt: 1 }}
            color="primary"
            variant="outlined"
          /> */}
        </Box>
      ))}
      {/* </Stack> */}
    </Box>
  );

  const renderContact = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Contact Support
      </Typography>

      {submitSuccess ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="success.main">
            Your support ticket has been submitted successfully! We'll get back
            to you within 24 hours.
          </Typography>
        </Box>
      ) : (
        <form onSubmit={handleTicketSubmit}>
          {/* <Stack spacing={3}> */}
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Name"
              value={ticket.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              value={ticket.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Box>

          <TextField
            label="Subject"
            value={ticket.subject}
            onChange={(e) => handleInputChange("subject", e.target.value)}
            required
            fullWidth
            size="small"
          />

          <TextField
            select
            label="Category"
            value={ticket.category}
            onChange={(e) => handleInputChange("category", e.target.value)}
            fullWidth
            size="small"
          >
            {supportCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </TextField>

          <TextField
            select
            label="Priority"
            value={ticket.priority}
            onChange={(e) =>
              handleInputChange(
                "priority",
                e.target.value as "low" | "medium" | "high"
              )
            }
            fullWidth
            size="small"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </TextField>

          <TextField
            label="Message"
            multiline
            rows={4}
            value={ticket.message}
            onChange={(e) => handleInputChange("message", e.target.value)}
            required
            fullWidth
            size="small"
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            // startIcon={
            //   isSubmitting ? <CircularProgress size={20} /> : <Send />
            // }
            sx={{ alignSelf: "flex-end" }}
          >
            {isSubmitting ? "Submitting..." : "Submit Ticket"}
          </Button>
          {/* </Stack> */}
        </form>
      )}
    </Box>
  );

  const renderResources = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Helpful Resources
      </Typography>

      <List>
        <ListItem button sx={{ borderRadius: 1, mb: 1 }}>
          <ListItemIcon>
            <Book color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Getting Started Guide"
            secondary="Step-by-step tutorial for new users"
          />
        </ListItem>

        <ListItem button sx={{ borderRadius: 1, mb: 1 }}>
          <ListItemIcon>{/* <VideoLibrary color="primary" /> */}</ListItemIcon>
          <ListItemText
            primary="Video Tutorials"
            secondary="Learn how to use Keep Kid Busy effectively"
          />
        </ListItem>

        <ListItem button sx={{ borderRadius: 1, mb: 1 }}>
          <ListItemIcon>{/* <Forum color="primary" /> */}</ListItemIcon>
          <ListItemText
            primary="Community Forum"
            secondary="Connect with other dads and share tips"
          />
        </ListItem>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Contact Information
        </Typography>

        <ListItem>
          <ListItemIcon>
            <Email color="primary" />
          </ListItemIcon>
          <ListItemText primary="support@keepkidbusy.com" />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <Phone color="primary" />
          </ListItemIcon>
          <ListItemText primary="1-800-KEEP-KID" />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <Chat color="primary" />
          </ListItemIcon>
          <ListItemText primary="Live Chat (Mon-Fri, 9AM-6PM EST)" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      {/* Floating Support Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          left: 24,
          zIndex: 1000,
        }}
      >
        {/* <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}> */}
        <Button
          variant="contained"
          onClick={handleOpen}
          startIcon={<Support />}
          sx={{
            bgcolor: "primary.main",
            color: "white",
            borderRadius: 3,
            px: 3,
            py: 1.5,
            boxShadow: theme.shadows[4],
            "&:hover": {
              bgcolor: "primary.dark",
              boxShadow: theme.shadows[8],
            },
          }}
        >
          Need Help?
        </Button>
        {/* </motion.div> */}
      </Box>

      {/* Support Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: "80vh" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" component="h2">
              <Support sx={{ mr: 1, verticalAlign: "middle" }} />
              Customer Support
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Tab Navigation */}
          <Box sx={{ mb: 3 }}>
            {[
              { key: "faq", label: "FAQ", icon: <QuestionAnswer /> },
              { key: "contact", label: "Contact", icon: <Email /> },
              { key: "resources", label: "Resources", icon: <Book /> },
            ].map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? "contained" : "outlined"}
                startIcon={tab.icon}
                onClick={() => setActiveTab(tab.key as any)}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          {/* Tab Content */}
          {/* <AnimatePresence mode="wait"> */}
          {/* <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            > */}
          {activeTab === "faq" && renderFAQ()}
          {activeTab === "contact" && renderContact()}
          {activeTab === "resources" && renderResources()}
          {/* </motion.div> */}
          {/* </AnimatePresence> */}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerSupport;
